import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReleaseNotesModal from './ReleaseNotesModal';
import '../styles/UserMenu.css';

function UserMenu({ userId, displayName, email, onLogout, releaseNotes, isAdmin = false,
                    aiEnabled = false, onOpenAiDisclaimer }) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showReleaseNotesModal, setShowReleaseNotesModal] = useState(false);
  const containerRef = useRef(null);

  const name = displayName || userId || 'User';
  // Two letters, like every circle on the presence rail — your own avatar sits next to
  // theirs, so it has to be drawn the same way. It stays SOLID purple where colleagues are
  // a light wash: that difference is what says "this one is you".
  const initials = (() => {
    const parts = String(name).replace(/[._]/g, ' ').split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return String(name).slice(0, 2).toUpperCase();
  })();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const hasReleases = releaseNotes?.releases?.length > 0;
  const appVersion = releaseNotes?.releases?.[0]?.version;
  const copyrightYear = new Date().getFullYear();

  const handleReleaseNotesClick = () => {
    setShowReleaseNotesModal(true);
    setIsOpen(false);
  };

  const handleLogoutClick = () => {
    if (onLogout && typeof onLogout === 'function') {
      onLogout();
    }
  };

  return (
    <div className="user-menu" ref={containerRef}>
      <button className="user-avatar" onClick={() => setIsOpen(!isOpen)} title={userId}>
        {initials}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          <div className="menu-user-info">
            <div className="menu-user-avatar">{initials}</div>
            <span className="menu-user-id">{name}</span>
          </div>
          {email && <div className="menu-user-email">{email}</div>}

          <div className="menu-divider"></div>

          {/* Preferences is a LINK, not a control. The settings themselves live on their
              own screen: the first one already needs three options with a sentence each,
              and a menu you have to scroll is a menu that has stopped being a menu. */}
          <button
            type="button"
            className="menu-item"
            onClick={() => { setIsOpen(false); navigate('/preferences'); }}
          >
            <svg className="menu-item-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.07a5.99 5.99 0 012.06.86l.76-.76a1 1 0 111.41 1.41l-.76.76c.4.62.7 1.31.86 2.06H16a1 1 0 110 2h-1.07a5.99 5.99 0 01-.86 2.06l.76.76a1 1 0 11-1.41 1.41l-.76-.76a5.99 5.99 0 01-2.06.86V16a1 1 0 11-2 0v-1.07a5.99 5.99 0 01-2.06-.86l-.76.76a1 1 0 01-1.41-1.41l.76-.76A5.99 5.99 0 015.07 11H4a1 1 0 110-2h1.07c.16-.75.46-1.44.86-2.06l-.76-.76a1 1 0 011.41-1.41l.76.76A5.99 5.99 0 019 4.07V3a1 1 0 011-1zm0 5a3 3 0 100 6 3 3 0 000-6z" clipRule="evenodd" />
            </svg>
            Preferences
          </button>

          <div className="menu-divider"></div>

          {isAdmin && (
            <>
              <div className="menu-section-label">Admin</div>
              <button
                type="button"
                className="menu-item"
                onClick={() => { setIsOpen(false); navigate('/settings'); }}
              >
                <svg className="menu-item-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Settings
              </button>
              <div className="menu-divider"></div>
            </>
          )}

          {hasReleases && (
            <button
              type="button"
              className="menu-item menu-item--release-notes"
              onClick={handleReleaseNotesClick}
            >
              <svg className="menu-item-icon" viewBox="0 0 20 20" fill="currentColor">
                <path d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" />
              </svg>
              Release Notes
            </button>
          )}

          {aiEnabled && onOpenAiDisclaimer && (
            <button
              type="button"
              className="menu-item menu-item--ai-disclaimer"
              onClick={() => { setIsOpen(false); onOpenAiDisclaimer(); }}
            >
              <svg className="menu-item-icon" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h5.172a2 2 0 011.414.586l2.828 2.828A2 2 0 0116 6.828V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm8 8a1 1 0 10-2 0v.01a1 1 0 102 0V12zm-1-5a1 1 0 00-1 1v2a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              AI Disclaimer
            </button>
          )}

          <div className="menu-divider"></div>

          <button
            type="button"
            className="menu-item menu-item--signout"
            onClick={handleLogoutClick}
          >
            <svg className="menu-item-icon" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z" clipRule="evenodd" />
            </svg>
            Sign out
          </button>

          <div className="menu-divider"></div>

          <div className="menu-footer">
            {appVersion && <span className="menu-footer-version">Version {appVersion}</span>}
            <span className="menu-footer-copyright">© Orcanos {copyrightYear}</span>
          </div>
        </div>
      )}

      {showReleaseNotesModal && (
        <ReleaseNotesModal
          releases={releaseNotes?.releases}
          onClose={() => setShowReleaseNotesModal(false)}
        />
      )}
    </div>
  );
}

export default UserMenu;
